import { z } from 'zod';
import { accessTokenCapabilities, accessTokenRelationships, profileCapabilities, profileRelationships, reportCapabilities, reportRelationships, } from './capabilities.js';
import { postCapabilities } from './capabilities.js';
import { postRelationships } from './capabilities.js';
import { additionalForbiddenHandlesSchema } from './forbiddenHandles.js';
export const passwordPlaceHolder = '*********';
export const password = (sanitize) => sanitize
    ? z
        .string()
        .transform(() => passwordPlaceHolder)
        .describe('password')
    : z.string();
export const fixed = (type, sanitize) => (sanitize ? type.describe('fixed') : type);
export const hidden = (type) => type.describe('hidden');
export const longText = (type) => type.describe('longText');
export const mediaStorageParamsSchema = z.object({
    path: z.string(),
    prefix: z.string(),
    host: z.string().optional(),
});
/** MiB. 1024 is 1 GiB — the default shown in the admin media section. */
export const DEFAULT_MAX_MEDIA_UPLOAD_MB = 1024;
export const mediaUploadLimitBytes = (maxUploadMb) => {
    const mb = Number.isInteger(maxUploadMb) && maxUploadMb >= 1
        ? maxUploadMb
        : DEFAULT_MAX_MEDIA_UPLOAD_MB;
    return mb * 1024 * 1024;
};
export const stripePaymentConfigSchema = (sanitize) => z.object({
    paidMembership: z
        .object({
        enabled: z.boolean(),
        productId: z.string().optional(),
        trialPeriodDays: z.number().optional(),
    })
        .optional(),
    publishableKey: password(sanitize).optional(),
    secretKey: password(sanitize).optional(),
    webhookSecret: password(sanitize).optional(),
});
export const emailConfigSchemaFactory = (sanitize) => z.object({
    service: z.string(),
    defaultTemplatePath: z.string(),
    defaultFrom: z.string().optional(),
    transportConfig: z.object({
        host: z.string(),
        port: z.number(),
        secure: z.boolean().default(true),
        // Optional so unauthenticated SMTP (e.g. Mailpit) can omit credentials.
        auth: z
            .object({
            user: z.string().optional(),
            pass: password(sanitize).optional(),
        })
            .optional(),
    }),
});
export const emailConfigSchema = emailConfigSchemaFactory(false);
export const emailConfigSanitizedSchema = emailConfigSchemaFactory(true);
export const coreConfigSchemaFactory = (sanitize) => z.object({
    version: fixed(z.string(), sanitize),
    environment: fixed(z.string(), sanitize),
    server: z.object({
        host: z.string(),
        signUpsOpen: z.boolean(),
        publicContent: z.boolean(),
        /** Cap on non-deleted profiles; 0 / unset = unlimited. Env-only (not admin-editable). */
        maxProfiles: fixed(z.number().int().nonnegative().optional(), sanitize),
    }),
    db: fixed(z.object({
        // Optional legacy fields so older stored configs still parse.
        url: z.string().url().optional(),
        databaseName: z.string().optional(),
        databaseUrl: z.string(),
    }), sanitize),
    redis: fixed(z.object({
        host: z.string(),
    }), sanitize),
    email: emailConfigSchemaFactory(sanitize).describe('hidden'),
    logs: z.object({
        local: fixed(z.object({
            path: z.string(),
        }), sanitize),
        retention: z.object({
            /** Age in days at which a daily log file is zipped. */
            zipAfterDays: z.number().int().min(1).default(7).catch(7),
            /** Age in days at which log files (and their zips) are deleted. */
            deleteAfterDays: z.number().int().min(1).default(30).catch(30),
        }),
    }),
    media: z.object({
        maxUploadMb: z
            .number()
            .int()
            .min(1)
            .default(DEFAULT_MAX_MEDIA_UPLOAD_MB)
            .catch(DEFAULT_MAX_MEDIA_UPLOAD_MB),
        storage: z.object({
            driver: z.enum(['openpeeps']),
            params: mediaStorageParamsSchema,
        }),
    }),
    network: z.object({
        port: z.number(),
    }),
    plugins: z.object({
        path: z.string(),
        rootPackageJsonPath: z.string(),
    }),
    secrets: z.object({
        jwt: password(sanitize),
    }),
    jams: z.object({
        livekit: z.object({
            url: z.string(),
            apiKey: z.string().optional(),
            apiSecret: password(sanitize).optional(),
            recordingEnabled: z.boolean().optional(),
        }),
    }),
    vapid: z.object({
        privateKey: password(sanitize).optional(),
        publicKey: z.string().optional(),
    }),
    federation: z.object({
        active: z.boolean(),
        /** Host patterns (`mastodon.social`, `*.example.com`). Empty = fail closed. */
        allowedHosts: z.array(z.string()).default([]),
    }),
    activityPub: z.object({
        defaultDomain: z.string(),
        itemsPerPage: z.number(),
        threadDepth: z.number(),
        federation: z.object({
            requestTimeout: z.number(),
        }),
    }),
    sso: z
        .object({
        onlySSO: z.boolean().optional(),
        generic: z
            .object({
            id: z.string().default('example-generic'),
            name: z.string().default('Generic SSO'),
            /** URL or path shown on the login page to start this provider's flow. */
            loginLink: z.string().optional(),
            userProfileRequest: z.object({
                url: z.string(),
                authHeader: z.string().optional(),
            }),
            userProfilePaths: z.object({
                email: z.string(),
                handle: z.string().optional(),
                avatar: z.string().optional(),
                displayName: z.string().optional(),
            }),
            createAccounts: z.boolean().optional(),
            createProfiles: z.boolean().optional(),
        })
            .default({
            id: 'example-generic',
            name: 'Generic SSO',
            loginLink: '',
            userProfileRequest: {
                url: 'https://example.com/profile',
                authHeader: 'Bearer ${token}',
            },
            userProfilePaths: {
                email: '$.data.account.email',
            },
            createAccounts: true,
            createProfiles: true,
        })
            .array(),
        oidc: z
            .object({
            id: z.string(),
            name: z.string(),
            kind: z
                .enum([
                'oidc',
                'github',
                'gitlab',
                'gitlab-selfhosted',
                'google',
                'entra',
                'auth0',
                'keycloak',
                'authentik',
                'okta',
                'discord',
                'pocketid',
            ])
                .optional(),
            instanceUrl: z.string().optional(),
            tenant: z.string().optional(),
            authorizationUrl: z.string(),
            tokenUrl: z.string(),
            userinfoUrl: z.string(),
            emailsUrl: z.string().optional(),
            jwksUri: z.string().optional(),
            clientId: z.string(),
            clientSecret: password(sanitize).optional(),
            scope: z.string().optional(),
            claimMapping: z
                .object({
                email: z.string().optional(),
                handle: z.string().optional(),
                displayName: z.string().optional(),
                avatar: z.string().optional(),
            })
                .optional(),
            approvalRequired: z.boolean().optional(),
        })
            .default({
            id: 'example-oidc',
            name: 'OIDC Provider',
            kind: 'oidc',
            authorizationUrl: 'https://example.com/authorize',
            tokenUrl: 'https://example.com/token',
            userinfoUrl: 'https://example.com/userinfo',
            clientId: '',
            scope: 'openid email profile',
            claimMapping: {
                email: 'email',
                handle: 'preferred_username',
                displayName: 'name',
                avatar: 'picture',
            },
            approvalRequired: false,
        })
            .array(),
    })
        .describe('hidden'),
    services: z.object({
        sentry: z.object({
            enabled: z.boolean(),
            dsn: z.string().optional(),
            replayEnabled: z.boolean().optional(),
        }),
        location: z.object({
            type: z.string(),
            url: z.string(),
            apiKey: password(sanitize).optional(),
        }),
    }),
    apps: z.object({
        ios: z.object({
            url: z.string().optional(),
            bundleIdentifier: z.string().optional(),
            teamId: z.string().optional(),
            appId: z.string().optional(),
            apnKeyId: z.string().optional(),
            apnSigningKey: longText(z.string()).optional(),
        }),
        android: z.object({
            url: z.string().optional(),
            packageName: z.string().optional(),
            appId: z.string().optional(),
            fcmCredentials: longText(z.string()).optional(),
        }),
    }),
    payments: z.object({
        stripe: stripePaymentConfigSchema(sanitize),
    }),
});
export const coreConfigSchema = coreConfigSchemaFactory(false);
export const coreConfigSanitizedSchema = coreConfigSchemaFactory(true);
/** Community-configured member profile fields (`profiles.additionalFields`). */
export const communityProfileAdditionalFieldSchema = z.object({
    // Trim so whitespace-only keys fail the same as "".
    key: z.string().trim().min(1),
    label: z.string(),
    icon: z.string().optional(),
    linkPattern: z.string().optional(),
});
export const communityConfigSchemaFactory = (_sanitize) => z.object({
    theme: z.object({
        base: z.string(),
        icon: z.string().optional(),
        mobileIcon: z.string().optional(),
        // Kept so /server/info still satisfies common 0.1.44 (required
        // theme.primaryHex) and RN 0.3.1 root-theme reads.
        primaryHex: z.string().optional(),
        logoFull: z.string().optional(),
        logoSmall: z.string().optional(),
        defaultProfileAvatar: z.string().optional(),
        defaultGroupAvatar: z.string().optional(),
        backgroundAuth: z.string().optional(),
        background: z.string().optional(),
        light: z.object({
            primaryHex: z.string(),
            secondaryHex: z.string().optional(),
            fontFamily: z.string().optional(),
            buttonRadius: z.string().optional(),
            radius: z.string().optional(),
            logoSmall: z.string().optional(),
            defaultProfileAvatar: z.string().optional(),
            defaultGroupAvatar: z.string().optional(),
            backgroundAuth: z.string().optional(),
            background: z.string().optional(),
        }),
        dark: z.object({
            primaryHex: z.string(),
            secondaryHex: z.string().optional(),
            fontFamily: z.string().optional(),
            buttonRadius: z.string().optional(),
            radius: z.string().optional(),
            logoSmall: z.string().optional(),
            defaultProfileAvatar: z.string().optional(),
            defaultGroupAvatar: z.string().optional(),
            backgroundAuth: z.string().optional(),
            background: z.string().optional(),
        }),
    }),
    profiles: z
        .object({
        additionalFields: z
            .array(communityProfileAdditionalFieldSchema)
            .optional(),
    })
        .optional(),
    info: z.object({
        name: z.string(),
        tagLine: z.string(),
        contactEmail: z.string().email().optional(),
        privacyPolicy: z.string().optional(),
        termsAndConditions: z.string().optional(),
    }),
    content: z.object({
        aboutPage: z.string().optional(),
        welcomePage: z.string().optional(),
        codeOfConduct: z.string().optional(),
        welcomeEmail: z.string().optional(),
        pinnedPost: z.string().optional(),
    }),
    settings: z.object({
        openRegistrations: z.boolean(),
        defaultLanguage: z.string().optional(),
        defaultTimeZone: z.string().optional(),
        defaultFeedFormat: z.enum(['threaded', 'linear']).optional(),
        /** Adds member profile pages to the sitemap. Off by default: a crawled
         *  profile is cached by search engines long after the member leaves. */
        indexProfiles: z.boolean().optional(),
        /**
         * Usernames this community blocks in addition to the built-in list.
         * Built-in names are not stored here and cannot be removed.
         */
        additionalForbiddenHandles: additionalForbiddenHandlesSchema.optional(),
    }),
    roles: z.object({
        onRegistration: z.object({
            add: z.string().array(),
            remove: z.string().array(),
        }),
        onEmailValidation: z.object({
            add: z.string().array(),
            remove: z.string().array(),
        }),
    }),
});
export const communityConfigSchema = communityConfigSchemaFactory(false);
export const communityConfigSanitizedSchema = communityConfigSchemaFactory(true);
const addWildCardOptions = (capabilities) => {
    const wildCard = '*';
    const firstLevelCategories = new Set(capabilities.map((capability) => capability.split('-')[0]));
    const secondLevelCategories = [...firstLevelCategories].map((flc) => new Set(capabilities
        .map((c) => c.split('-'))
        .filter((clist) => clist[0] === flc)
        .map((clist) => `${flc}-${clist[1]}`)));
    return [
        wildCard,
        ...capabilities,
        [...firstLevelCategories].map((flc) => `${flc}-${wildCard}`),
        [...secondLevelCategories].flatMap((slc) => [
            ...slc,
            ...[...slc].map((key) => `${key}-${wildCard}`),
        ]),
    ].flat();
};
export const capabilitiesSchema = z
    .object({
    add: z.string().array().optional(),
    remove: z.string().array().optional(),
})
    .optional();
const capabilitiesGroupSchema = (capabilities) => z
    .object({
    add: z
        .array(z.enum(addWildCardOptions(capabilities)))
        .optional()
        .default([]),
    remove: z
        .array(z.enum(addWildCardOptions(capabilities)))
        .optional()
        .default([]),
})
    .optional()
    .default({ add: [], remove: [] });
const relationshipsGroupSchema = (relationships, capabilities) => z.object(Object.fromEntries(relationships.map((relationship) => [
    relationship,
    capabilitiesGroupSchema(capabilities),
])));
export const capabilitiesConfigSchema = z.object({
    post: relationshipsGroupSchema(postRelationships, [...postCapabilities]),
    profile: relationshipsGroupSchema(profileRelationships, [
        ...profileCapabilities,
        ...postCapabilities,
    ]),
    report: relationshipsGroupSchema(reportRelationships, [
        ...reportCapabilities,
    ]),
    accessToken: relationshipsGroupSchema(accessTokenRelationships, [
        ...accessTokenCapabilities,
    ]),
});
export const capabilitiesConfigSchemaFactory = (_sanitize) => z.object({
    post: relationshipsGroupSchema(postRelationships, [...postCapabilities]),
    profile: relationshipsGroupSchema(profileRelationships, [
        ...profileCapabilities,
        ...postCapabilities,
    ]),
    report: relationshipsGroupSchema(reportRelationships, [
        ...reportCapabilities,
    ]),
    accessToken: relationshipsGroupSchema(accessTokenRelationships, [
        ...accessTokenCapabilities,
    ]),
});
